// Register components 'globally'. These components can be used without importing into each component explicitly.

import type { DefineComponent, VueConstructor } from "vue";
import Vue from "vue";
import BmBtn from "@/components/controls/input/BmBtn.vue";
import BmCheckbox from "@/components/controls/input/BmCheckbox.vue";
import BmCheckboxGroup from "@/components/controls/input/BmCheckboxGroup.vue";
import BmDate from "@/components/controls/input/BmDate.vue";
import BmDateTime from "@/components/controls/input/BmDateTime.vue";
import BmDateRange from "@/components/controls/daterange/BmDateRange.vue";
import BmRadio from "@/components/controls/input/BmRadio.vue";
import BmRadioGroup from "@/components/controls/input/BmRadioGroup.vue";
import BmSlider from "@/components/controls/input/BmSlider.vue";
import BmSwitch from "@/components/controls/input/BmSwitch.vue";
import BmText from "@/components/controls/input/BmText.vue";
import BmNumber from "@/components/controls/input/BmNumber.vue";
import BmTextArea from "@/components/controls/input/BmTextArea.vue";
import BmTime from "@/components/controls/input/BmTime.vue";
import BmDropdown from "@/components/controls/input/BmDropdown.vue";
import BmEnumDdl from "@/components/controls/input/BmEnumDdl.vue";
import BmCard from "@/components/controls/card/BmCard.vue";
import BmCollapsibleCard from "@/components/controls/card/BmCollapsibleCard.vue";
import BmEditCard from "@/components/controls/card/BmEditCard.vue";
import BmCardSubtitle from "@/components/controls/card/BmCardSubtitle.vue";
import BmSubpageHeading from "@/components/controls/subpage/BmSubpageHeading.vue";
import BmSubpageDescription from "@/components/controls/subpage/BmSubpageDescription.vue";
import BmField from "@/components/controls/BmField.vue";
import BmFieldList from "@/components/controls/BmFieldList.vue";
import BmPage from "@/components/pages/BmPage.vue";
import BmPageFooter from "@/components/pages/BmPageFooter.vue";
import BmQuantityGrid from "@/components/controls/quantitygrid/BmQuantityGrid.vue";
import BmControlList from "@/components/controls/controllist/BmControlList.vue";
import BmEditBox from "@/components/controls/editbox/BmEditBox.vue";
import BmImageUploaderById from "@/components/controls/input/BmImageUploaderById.vue";
import BmReadOnlyField from "@/components/controls/input/BmReadOnlyField.vue";
import BmList from "@/components/controls/listselector/BmList.vue";
import BmListSelector from "@/components/controls/listselector/BmListSelector.vue";
import BmTabs from "@/components/controls/tabs/BmTabs.vue";
import BmColumnHint from "@/components/controls/wizard/BmColumnHint.vue";
import BmWizard from "@/components/controls/wizard/BmWizard.vue";
import BmWizardFooter from "@/components/controls/wizard/BmWizardFooter.vue";
import BmWizardFormElement from "@/components/controls/wizard/BmWizardFormElement.vue";
import BmWizardStep from "@/components/controls/wizard/BmWizardStep.vue";
import BmAlert from "@/components/controls/BmAlert.vue";
import BmForm from "@/components/controls/BmForm.vue";
import BmPageStatusSelect from "@/components/pages/BmPageStatusSelect.vue";
import BmTooltip from "@/components/controls/input/BmTooltip.vue";
import BmGrid from "@/components/controls/grid/BmGrid.vue";
import BmGridControlPanel from "@/components/controls/grid/BmGridControlPanel.vue";
import BmGridPage from "@/components/pages/BmGridPage.vue";
import BmCurrency from "@/components/controls/input/specialised/BmCurrency.vue";
import BmVolumeGeneral from "@/components/controls/input/specialised/BmVolumeGeneral.vue";
import BmVolumeVessels from "@/components/controls/input/specialised/BmVolumeVessels.vue";
import BmUnitBase from "@/components/controls/input/specialised/BmUnitBase.vue";
import BmWeight from "@/components/controls/input/specialised/BmWeight.vue";
import BmPercentage from "@/components/controls/input/specialised/BmPercentage.vue";
import BmAlcoholContent from "@/components/controls/input/specialised/BmAlcoholContent.vue";
import BmBottomSheet from "@/components/dialogs/BmBottomSheet.vue";
import BmBtnExpand from "@/components/controls/input/BmBtnExpand.vue";
import BmControl from "@/components/controls/input/BmControl.vue";
import BmCountry from "@/components/controls/input/specialised/BmCountry.vue";
import BmBreadcrumbs from "@/components/controls/app/BmBreadcrumbs.vue";
import BmTelephone from "@/components/controls/input/specialised/BmTelephone.vue";
import BmChip from "@/components/controls/BmChip.vue";
import BmDialog from "@/components/dialogs/BmDialog.vue";
import BmDialogControls from "@/components/dialogs/BmDialogControls.vue";
import BmBarcodeInput from "@/components/controls/device/BmBarcodeInput.vue";
import BmSignature from "@/components/controls/input/specialised/BmSignature.vue";
import BmPlato from "@/components/controls/input/specialised/BmPlato.vue";
import BmTimeline from "@/components/controls/timeline/BmTimeline.vue";
import BmColourPicker from "@/components/controls/input/BmColourPicker.vue";
import BmEmail from "@/components/controls/input/specialised/BmEmail.vue";
import { ButtonBar } from "@/components/platform/components/buttonbar/buttonBar";

import DaNumber from "@da/components/controls/DaNumber.vue";
import BmFormFlowLayout from "@/components/controls/BmFormFlowLayout.vue";

export function registerComponents(vue: VueConstructor) {
  // Cards
  vue.component("BmCard", BmCard);
  vue.component("BmCollapsibleCard", BmCollapsibleCard);
  vue.component("BmEditCard", BmEditCard);
  vue.component("BmCardSubtitle", BmCardSubtitle);

  // Subpage
  vue.component("BmSubpageHeading", BmSubpageHeading);
  vue.component("BmSubpageDescription", BmSubpageDescription);

  // Control List
  vue.component("BmControlList", BmControlList);

  // EditBox
  vue.component("BmEditBox", BmEditBox);

  // Grid
  // Components with mixins and `defineComponent` messes with the types for defineComponent and what `Vue.component` seems to support. We know this component works
  // and there are no issues with it. Will forcefully coearce the type for simplicity and known to only be a type issue.
  //
  // Swapping to use `Vue.extends` caused more issues around the `default` prop definition where the factory function for it would affect the type
  // IE: PropType<() => boolean> with a default: () => true would give (() => boolean) | boolean
  // whereas we just want () => boolean. Changing to `() => () => true` would fix the type errors, but break the usage...
  vue.component("BmGrid", BmGrid as unknown as DefineComponent);
  vue.component("BmGridControlPanel", BmGridControlPanel);

  // Input
  // TSC seems to get the types fractionally confused and does not think a component created with defineComponent is suitable for
  // use in vue.component. We will mark the lines with expect error. If these components get updated to script-setup
  // then TSC will no longer be confused and expect-error no longer required.
  // @ts-expect-error
  vue.component("BmBtn", BmBtn);
  vue.component("BmBtnExpand", BmBtnExpand);

  // Similar to BmGrid, could swap to Vue.extends but match fix above instead as this is only a type issue
  vue.component("BmCheckbox", BmCheckbox as unknown as DefineComponent);

  vue.component("BmCheckboxGroup", BmCheckboxGroup);
  vue.component("BmDate", BmDate);
  vue.component("BmDropdown", BmDropdown);
  vue.component("BmEnumDdl", BmEnumDdl);
  vue.component("BmImageUploaderById", BmImageUploaderById);
  vue.component("BmDateTime", BmDateTime);
  vue.component("BmNumber", BmNumber);
  // @ts-expect-error
  vue.component("BmRadio", BmRadio);
  // @ts-expect-error
  vue.component("BmRadioGroup", BmRadioGroup);
  // @ts-expect-error
  vue.component("BmReadOnlyField", BmReadOnlyField);
  vue.component("BmControl", BmControl);
  // @ts-expect-error
  vue.component("BmSlider", BmSlider);
  vue.component("BmSwitch", BmSwitch);
  vue.component("BmText", BmText);
  vue.component("BmTextArea", BmTextArea);
  vue.component("BmTime", BmTime);
  vue.component("BmTooltip", BmTooltip);

  vue.component("BmCurrency", BmCurrency);
  vue.component("BmVolumeGeneral", BmVolumeGeneral);
  vue.component("BmVolumeVessels", BmVolumeVessels);
  vue.component("BmUnitBase", BmUnitBase);
  vue.component("BmWeight", BmWeight);
  vue.component("BmPercentage", BmPercentage);
  vue.component("BmAlcoholContent", BmAlcoholContent);
  vue.component("BmTelephone", BmTelephone);
  vue.component("BmCountry", BmCountry);
  vue.component("BmPlato", BmPlato);
  vue.component("BmColourPicker", BmColourPicker);
  vue.component("BmDateRange", BmDateRange);
  vue.component("BmEmail", BmEmail);

  // List Selectors
  vue.component("BmList", BmList);
  vue.component("BmListSelector", BmListSelector);

  // Quantity Grid
  vue.component("BmQuantityGrid", BmQuantityGrid);

  // Tabs
  vue.component("BmTabs", BmTabs);

  // Wizard
  vue.component("BmColumnHint", BmColumnHint);
  vue.component("BmWizard", BmWizard);
  vue.component("BmWizardFooter", BmWizardFooter);
  vue.component("BmWizardFormElement", BmWizardFormElement);
  vue.component("BmWizardStep", BmWizardStep);

  // Root
  vue.component("BmAlert", BmAlert);
  vue.component("BmField", BmField);
  vue.component("BmFieldList", BmFieldList);
  vue.component("BmForm", BmForm);
  vue.component("BmFormFlowLayout", BmFormFlowLayout);

  // Pages
  vue.component("BmPage", BmPage);
  vue.component("BmPageFooter", BmPageFooter);
  vue.component("BmPageStatusSelect", BmPageStatusSelect);
  vue.component("BmGridPage", BmGridPage);
  vue.component("ButtonBar", ButtonBar);

  // Breadcrumbs
  vue.component("BmBreadcrumbs", BmBreadcrumbs);

  // Info
  vue.component("BmChip", BmChip);

  // Sheet
  vue.component("BmBottomSheet", BmBottomSheet);

  // Delivery App Number
  vue.component("DaNumber", DaNumber);

  // Barcode Input - Scanners
  vue.component("BmBarcodeInput", BmBarcodeInput);

  // Dialog
  vue.component("BmDialog", BmDialog);
  vue.component("BmDialogControls", BmDialogControls);

  // Signature Control
  vue.component("BmSignature", BmSignature);
  //vue.use(VueSignaturePad); // We need to register this, or it won't be available on all pages

  // Timeline
  vue.component("BmTimeline", BmTimeline);
}

// Note: This can be problematic if you're also using `Vue.use(CompositionAPI)`,
// don't import this field before that's completed as this will be run out of context
registerComponents(Vue);
